import { useNavigate } from "react-router-dom";
import { Fragment, useEffect } from "react";
import { toast } from 'react-toastify';

import Tab, { TabProps } from "../../../molecules/Tab";

import PageForm from "../../../templates/PageForm";
import { computed, useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";

import { removeErrorField } from "../../../../utils/formErrorUtils";
import LegalPersonFormTab1 from "./LegalPersonFormTab1";
import useCities from "../../../../hooks/useCities";
import useProvinces from "../../../../hooks/useProvinces";

const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/legal-persons`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

type Form = {
    govTaxId: string;
    fantasyName: string;
    legalName: string;
    zipcode: string;
    street: string;
    streetType: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
}

const LegalPersonPageForm = () => {

    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();

    const navigate = useNavigate();
    const isSendingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');
    const isLoadingForm = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);

    const form = useSignal<Form>({
        govTaxId: '',
        fantasyName: '',
        legalName: '',
        zipcode: '',
        street: '',
        streetType: '',
        number: '',
        complement: '',
        district: '',
        cityId: '',
    });

    const tabItems: TabProps[] = [
        { id: "tab-1", title: 'Dados cadastrais', index: 0 },
    ];

    useEffect(() => {
        loadProvinces();
    }, []);

    const loadProvinces = async () => {
        isLoadingForm.value = true;
        try {
            provinces.value = await fetchProvinces();
        } catch (error) {
            toast.error("Erro ao carregar os estados.");
        }
        isLoadingForm.value = false;
    };

    const fetchCitiesWrapper = async (province: string) => {
        try {
            isLoadingForm.value = true;
            cities.value = await fetchCities(province);
        } catch (error) {
            toast.error("Erro ao carregar as cidades. Tente novamente!");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const cancelForm = () => {
        navigate("/person/partners/0/100/id/ASC/");
    };

    const submitForm = async () => {
        isSendingForm.value = true;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(form.value),
            });

            if (response.ok) {
                toast.success('Parceira cadastrada com sucesso!');
                navigate('/person/partners/0/100/id/DESC/');
            } else if (response.status === 401) {
                toast.error('A sessão expirou. Faça login novamente.');
                navigate('/login');
            } else if (response.status === 409) {
                const { errors, message } = await response.json();
                toast.error(message);
                errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
            } else {
                toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
            }
        } catch (error) {
            toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        }
        isSendingForm.value = false;
    };

    const saveButtonDisable = computed(() => {
        return (isSendingForm.value || form.value.govTaxId === '' || form.value.fantasyName === '' ||
            form.value.legalName === '' || form.value.zipcode === '' || form.value.street === '' || form.value.streetType === '' ||
            form.value.number === '' || form.value.district === '' || form.value.cityId === '');
    });

    const changeForm = (field: string, value: string) => {
        form.value = {
            ...form.value,
            [field]: value,
        };
        removeErrorField(errorFields.value, field);
    };

    return (
        <PageForm
            pageTitle={"Nova parceira"}
            apiVersion={apiVersion.value}
            isLoading={isSendingForm.value || isLoadingForm.value}
            breadcrumb={[
                { label: "Listagem de parceiras", url: "/person/partners/0/100/id/ASC/" },
                { label: "Nova parceira", url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">

                    <button
                        id="btn-cancel-legal-person"
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={cancelForm}
                        title="Cancelar registro"
                        disabled={isSendingForm.value}
                    >
                        Cancelar
                    </button>
                    <button
                        id="btn-save-legal-person"
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={submitForm}
                        title="Salvar registro"
                        disabled={saveButtonDisable.value}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <Fragment>
                <Tab tabItems={tabItems} tabIndex={0}></Tab>
                <div id="tab-content" className="overflow-y-auto">
                    <LegalPersonFormTab1
                        key="tab1"
                        form={form.value}
                        errorFields={errorFields.value}
                        changeForm={changeForm}
                        provinces={provinces.value}
                        cities={cities.value}
                        changeProvince={fetchCitiesWrapper}
                    />
                </div>
            </Fragment>

        </PageForm>
    );
};


export default LegalPersonPageForm;